import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Compass, Loader2, MapPin, CalendarDays, BadgeCheck, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";

const Day = ({ d }) => (
  <section className="flex gap-4 p-5 sm:p-6 rounded-2xl border border-black/5 bg-white/60" data-testid={`public-day-${d.day}`}>
    <div className="w-12 h-12 rounded-full bg-[#1A3B2B] text-[#FDFBF7] flex items-center justify-center text-xs font-semibold shrink-0 text-center leading-tight">Day {d.day}</div>
    <div className="min-w-0 flex-1">
      <h2 className="font-display text-xl sm:text-2xl">{d.title}</h2>
      {(d.date || d.city) && <p className="text-xs text-muted-foreground mb-2">{[d.date, d.city].filter(Boolean).join(" · ")}</p>}
      <ul className="divide-y divide-black/5">
        {d.activities.length
          ? d.activities.map((a, i) => (
              <li key={i} className="grid grid-cols-[62px_1fr] gap-2 py-2 text-sm">
                <span className="text-[#C8553D] font-semibold text-xs pt-0.5">{a.start}</span>
                <span>
                  {a.name} {a.verified && <BadgeCheck className="inline w-3.5 h-3.5 text-[#1A3B2B]" title="Verified against Google Places" />}
                  {a.address && <span className="block text-xs text-muted-foreground">{a.address}</span>}
                </span>
              </li>
            ))
          : ["morning", "afternoon", "evening"].filter((k) => d[k]).map((k) => (
              <li key={k} className="grid grid-cols-[80px_1fr] gap-2 py-2 text-sm"><span className="text-[#C8553D] font-semibold text-xs capitalize pt-0.5">{k}</span><span>{d[k]}</span></li>
            ))}
      </ul>
    </div>
  </section>
);

export default function PublicTrip() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/t/${slug}`).then((r) => setV(r.data)).catch(() => setError(true));
  }, [slug]);

  return (
    <div className="min-h-screen grain relative" data-testid="public-trip-page">
      <Seo title={v ? v.name : "Shared trip"} description={v?.description || "A shared WanderlyMe itinerary."} path={`/t/${slug}`} type="article" noindex />
      <header className="border-b border-black/5 px-6 h-16 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2"><Compass className="w-5 h-5 text-[#C8553D]" /><span className="font-display text-xl">WanderlyMe</span></Link>
        <Link to="/plan" className="px-5 py-2 text-sm rounded-full bg-[#1A3B2B] text-[#FDFBF7]" data-testid="public-trip-plan-cta">Plan my trip</Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        {error && (
          <div className="text-center py-20" data-testid="public-trip-404">
            <p className="font-display text-3xl mb-2">This trip isn't available</p>
            <p className="text-muted-foreground mb-6">The link may be wrong or the owner has turned sharing off.</p>
            <Link to="/plan" className="text-[#C8553D] underline underline-offset-4">Plan your own trip with WanderlyMe</Link>
          </div>
        )}
        {!v && !error && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
        {v && (
          <>
            <p className="text-xs uppercase tracking-widest text-[#C8553D] mb-3">Shared itinerary · read-only</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3" data-testid="public-trip-title">{v.name}</h1>
            <p className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-6">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {v.destination}</span>
              <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {v.days} day{v.days !== 1 && "s"} · {v.dates}</span>
            </p>
            {v.tagline && <p className="text-lg mb-2">{v.tagline}</p>}
            {v.has_cover && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/t/${slug}/cover`} alt={v.destination} className="w-full h-72 object-cover rounded-3xl border border-black/5 mb-6" />}
            {v.summary && <p className="text-muted-foreground mb-8">{v.summary}</p>}
            <div className="space-y-4">{v.itinerary.map((d) => <Day key={d.day} d={d} />)}</div>
            <div className="mt-10 rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-8 text-center">
              <h2 className="font-display text-3xl mb-2">Plan your own trip like this</h2>
              <p className="opacity-85 mb-5">Places verified against Google, routes that make sense on a map, and friends who can edit alongside you.</p>
              <Link to="/plan" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8553D] text-[#FDFBF7]" data-testid="public-trip-cta">Start planning <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
